// pages/Community/SkoolWelcomes.jsx
//
// The public welcome we left for each new member of our own community, and
// where that member stands NOW (prospect state) — who went on to engage, book
// or buy, and who never answered — with the override.
import React, { useEffect, useState, useCallback, useMemo } from "react";
import addNotification from "helpers/notify";
import { getSkoolWelcomes, setSkoolProspectState } from "api/connectors/connectors";
import { COLUMNS as PROSPECT_STATES } from "./SkoolProspects";

const PROSPECT = Object.fromEntries(PROSPECT_STATES.map((c) => [c.key, c]));
const SOURCE_LABEL = { manual: "set by you", booking: "booking", rule: "rule", ai: "AI" };
const STATUS = {
  success: { label: "Posted", fg: "#1a7f37", bg: "#e7f7ec" },
  queued: { label: "Queued", fg: "#9a6700", bg: "#fff4e5" },
  running: { label: "Posting", fg: "#9a6700", bg: "#fff4e5" },
  held: { label: "Awaiting approval", fg: "#0b3d91", bg: "#eef4ff" },
  failed: { label: "Failed", fg: "#b42318", bg: "#fdecea" },
  cancelled: { label: "Cancelled", fg: "#667085", bg: "#f2f3f5" },
};
const fmt = (d) => { try { return d ? new Date(d).toLocaleString() : "—"; } catch (_) { return "—"; } };
const Pill = ({ label, fg, bg, title, testid }) => (
  <span data-testid={testid} title={title} style={{ fontSize: "0.68rem", fontWeight: 600, color: fg, background: bg, borderRadius: 10, padding: "1px 8px", whiteSpace: "nowrap" }}>{label}</span>
);

export default function SkoolWelcomes({ brandId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState("");
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSkoolWelcomes(brandId ? { brandId } : {});
      setRows((res && res.welcomes) || []);
    } catch (e) {
      addNotification(e?.message || e?.error || "Could not load welcome comments", "error");
    } finally { setLoading(false); }
  }, [brandId]);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    if (!filter) return rows;
    if (filter === "untracked") return rows.filter((r) => !r.prospect);
    return rows.filter((r) => r.status === filter || (r.prospect && r.prospect.state === filter));
  }, [rows, filter]);

  // Where the welcomed members ended up — the point of the view.
  const tally = useMemo(() => {
    const t = {};
    rows.forEach((r) => { const k = r.prospect ? r.prospect.state : "untracked"; t[k] = (t[k] || 0) + 1; });
    return t;
  }, [rows]);

  const changeState = async (p, state) => {
    if (!p || !state) return;
    try {
      await setSkoolProspectState(p._id, state, state === "auto" ? "" : "set from Skool welcomes");
      addNotification(state === "auto" ? "Handed back to the classifier" : `Now: ${(PROSPECT[state] || {}).label || state}`, "success");
      load();
    } catch (e) { addNotification(e?.error || e?.message || "Could not change the state", "error"); }
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-2" style={{ gap: 10, flexWrap: "wrap" }}>
        <h6 className="mb-0">Skool welcome comments</h6>
        <span className="text-muted small" data-testid="welcome-tally">
          {rows.length} members welcomed · {PROSPECT_STATES.filter((c) => tally[c.key]).map((c) => `${tally[c.key]} ${c.label.toLowerCase()}`).join(" · ")}
          {tally.untracked ? ` · ${tally.untracked} not tracked` : ""}
        </span>
        <select className="form-control form-control-sm ml-auto" style={{ width: 210 }} aria-label="Welcomes filter" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All welcomes</option>
          {PROSPECT_STATES.map((c) => <option key={c.key} value={c.key}>Now: {c.label}</option>)}
          <option value="untracked">Not tracked</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>Comment: {v.label}</option>)}
        </select>
        <button className="btn btn-link btn-sm p-0" onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</button>
      </div>

      {!shown.length && !loading && <div className="text-muted small">{rows.length ? "Nothing matches this filter." : "No welcome comments for this brand yet."}</div>}

      {shown.map((r) => {
        const st = STATUS[r.status] || STATUS.cancelled;
        const p = r.prospect;
        const pc = p ? (PROSPECT[p.state] || PROSPECT.new) : null;
        const isOpen = open === r._id;
        return (
          <div key={r._id} data-testid={`welcome-${r._id}`} style={{ border: "1px solid #e4e7ec", borderRadius: 8, padding: "8px 10px", marginBottom: 8, background: "#fff" }}>
            <div className="d-flex align-items-center" style={{ gap: 8, flexWrap: "wrap", cursor: "pointer" }} onClick={() => setOpen(isOpen ? "" : r._id)}>
              <b className="small">{r.member || "(member)"}</b>
              {pc && <Pill testid="welcome-prospect-pill" label={`${pc.label}${p.stateSource === "manual" ? " ✎" : ""}`} fg={pc.color} bg={pc.bg}
                title={`${pc.label} (${SOURCE_LABEL[p.stateSource] || p.stateSource})${p.reason ? ` — ${p.reason}` : ""}`} />}
              {p && p.introduced && <Pill label="Introduced" fg="#1a7f37" bg="#e7f7ec" />}
              <Pill label={st.label} fg={st.fg} bg={st.bg} title={r.error || ""} />
              {r.attempts > 1 && <span className="text-muted small" data-testid="welcome-attempts" title={`${r.failedAttempts} failed`}>{r.attempts} attempts</span>}
              <span className="text-muted small ml-auto">{fmt(r.createdAt)}</span>
            </div>
            <div className="small text-muted" style={{ marginTop: 4 }}>
              {isOpen ? null : `${r.comment.slice(0, 160)}${r.comment.length > 160 ? "…" : ""}`}
            </div>
            {isOpen && (
              <div style={{ marginTop: 6 }}>
                <div className="small" style={{ background: "#eef4ff", borderRadius: 8, padding: "6px 8px", whiteSpace: "pre-wrap" }}>
                  <b>Our welcome:</b> {r.comment || "—"}
                </div>
                {r.profileUrl && <a className="small" href={r.profileUrl} target="_blank" rel="noreferrer">@{r.handle}</a>}
                {r.error && <div className="small" style={{ color: "#b42318" }}>{r.error}</div>}
                {pc ? (
                  <div data-testid="welcome-prospect-block" style={{ marginTop: 6, padding: 8, borderRadius: 8, background: pc.bg, fontSize: "0.78rem" }}>
                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                      <b style={{ color: pc.color }}>{pc.label}</b>
                      <span className="text-muted">· {SOURCE_LABEL[p.stateSource] || p.stateSource}</span>
                      <select className="form-control form-control-sm ml-auto" style={{ width: 180 }} aria-label="Change prospect state" value=""
                        onChange={(e) => changeState(p, e.target.value)}>
                        <option value="">Change state…</option>
                        {PROSPECT_STATES.filter((x) => x.key !== p.state || p.stateSource !== "manual").map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                        {p.stateSource === "manual" && <option value="auto">Back to automatic</option>}
                      </select>
                    </div>
                    {p.reason && <div style={{ color: "#475467" }}>{p.reason}</div>}
                    {p.bookingAt && <div style={{ color: "#6a3fb5" }}>Call booked: {fmt(p.bookingAt)}</div>}
                    <div className="text-muted">{pc.hint}</div>
                  </div>
                ) : (
                  <div className="text-muted small" style={{ marginTop: 6 }}>Not tracked — the classifier covers members who joined or talked to us in the last 30 days.</div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
